import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import { adjustReasons, blendedRate, can, signedPercent, statusLabels } from '@zilian/contracts';
import { api, download, imageUrl } from '../api';
import { usePrintSheet } from '../PrintSheet';
import LineQuantityDialog from '../LineQuantityDialog';
import ReviseDialog from '../ReviseDialog';
import ShareCodeDialog from '../ShareCodeDialog';
import PayDialog, { PAY_METHODS } from '../PayDialog';
import ReceiveDialog from '../ReceiveDialog';
import {
  amount,
  date,
  money,
  unitPrice,
  AccessoryPanel,
  accessoriesText,
  Status,
  Empty,
  type Props,
  type Field,
  QTY_DECIMALS,
  Tag,
  type StatusTone,
  pickupText,
  AdjustRows,
} from '../ui';

const STEPS = ['双方确认', '发货', '收货验收', '对账', '结清'];

function Panel(props: {
  title: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
  id?: string;
  highlight?: boolean;
}) {
  return (
    <Paper
      id={props.id}
      sx={{
        mb: 3,
        transition: 'box-shadow .3s',
        boxShadow: props.highlight ? '0 0 0 2px #e09a2b' : undefined,
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid #edf1f2',
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 650 }}>{props.title}</Typography>
        {props.extra}
      </Box>
      {props.children}
    </Paper>
  );
}

/** Two-tone bar: light for shipped, solid for accepted. */
function DeliveryBar({ shipped, accepted }: { shipped: number; accepted: number }) {
  return (
    <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: '#edf1f2' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${Math.min(100, shipped)}%`,
          borderRadius: 3,
          bgcolor: '#a7cfc6',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${Math.min(100, accepted)}%`,
          borderRadius: 3,
          bgcolor: 'primary.main',
        }}
      />
    </Box>
  );
}

export function OrderDetail(p: Props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null),
    [files, setFiles] = useState<any[]>([]),
    [shipping, setShipping] = useState(false),
    [revising, setRevising] = useState(false),
    [paying, setPaying] = useState(false),
    [receiving, setReceiving] = useState(false),
    [sharing, setSharing] = useState(false),
    [menu, setMenu] = useState<HTMLElement | null>(null),
    [flash, setFlash] = useState(false);
  const issuesRef = useRef<HTMLDivElement>(null);
  const printer = usePrintSheet();
  useEffect(() => {
    let live = true;
    setOrder(null);
    Promise.all([api(`/orders/${id}`), api<any[]>(`/orders/${id}/attachments`)])
      .then(([o, f]) => {
        if (live) {
          setOrder(o);
          setFiles(f);
        }
      })
      .catch((e) => p.setError(e.message));
    // Coming back to the tab (e.g. after the partner acted elsewhere) quietly picks up the
    // latest state, so actions are not offered on an outdated order.
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      api(`/orders/${id}`)
        .then((o) => live && setOrder(o))
        .catch(() => {});
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      live = false;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [id, p.version, p.workspace]);
  if (!order)
    return (
      <Box sx={{ p: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );

  const seller = order.seller_id === p.workspace;
  const peer = seller ? order.buyer_name : order.seller_name;
  const endpoint = (suffix: string, body: any) => api(`/orders/${id}/${suffix}`, body);
  const action = (
    title: string,
    fields: Field[],
    form: Record<string, any>,
    submit: (form: any, key: string) => Promise<unknown>,
    description?: string,
  ) => p.open({ title, fields, form, submit, description });
  const warehouseField: Field = { name: 'warehouseId', label: '本企业仓库', options: p.warehouses };
  const selectedWarehouse = p.warehouses[0]?.value;

  // Back to the list this member opened it from: their side's list, or 仓库作业 for someone who
  // only checks this side's goods in or out.
  const roles: string[] = p.current?.roles ?? [];
  const backTo = can(roles, seller ? 'sales' : 'purchase')
    ? { path: seller ? '/sales' : '/purchases', label: `${seller ? '销售' : '采购'}管理` }
    : { path: '/warehouse', label: '仓库作业' };
  // ---- Derived state -------------------------------------------------------------------
  // The server leaves money out on a side the member does not see money on (e.g. sales staff who
  // also check in purchases): then the page shows goods and delivery only, no money at all.
  const priced = order.total_amount !== undefined;
  const steps = priced ? STEPS : STEPS.slice(0, 3);
  const total = Number(order.total_amount);
  const goods = Number(order.goods_amount);
  const adjustment = Math.round((total - goods) * 100) / 100;
  const paid = (order.payments ?? []).reduce((sum: number, r: any) => sum + Number(r.amount), 0);
  // Off-platform payments declared by the buyer; the supplier confirms each against its account.
  const claims: any[] = order.claims ?? [];
  const pendingClaims = claims.filter((c) => c.status === 'CLAIMED');
  const pendingTotal = pendingClaims.reduce((sum, c) => sum + Number(c.amount), 0);
  const claimedTotal = claims
    .filter((c) => c.status !== 'REJECTED')
    .reduce((sum, c) => sum + Number(c.amount), 0);
  const payLeft = Math.max(0, Number(order.settlement?.amount ?? 0) - claimedTotal);
  // Orders on a monthly statement are confirmed and paid on the statement.
  const onStatement: { id: string; number: string; status: string } | null = order.statement;
  const mineConfirmed = seller ? order.seller_confirmed : order.buyer_confirmed;
  const settled = !!order.settlement?.seller_confirmed && !!order.settlement?.buyer_confirmed;
  const settlementMine = seller
    ? order.settlement?.seller_confirmed
    : order.settlement?.buyer_confirmed;
  const shipLeft = (l: any) => Number(l.quantity) + Number(l.replacement) - Number(l.shipped);
  const shipDone = order.lines.every((l: any) => shipLeft(l) <= 0);
  const delivered = order.status === 'CLOSED';
  const pendingReceipts = order.shipments.filter((s: any) => !s.finalized);
  const openReturns = order.returns.filter((r: any) =>
    ['REQUESTED', 'APPROVED', 'SHIPPED'].includes(r.status),
  );
  const myDiscrepancies = order.discrepancies.filter(
    (d: any) => !(seller ? d.seller_approved : d.buyer_approved),
  );
  const myReturns = openReturns.filter((r: any) =>
    seller ? ['REQUESTED', 'APPROVED', 'SHIPPED'].includes(r.status) : r.status === 'APPROVED',
  );
  const lineName = (lineId: string) =>
    order.lines.find((l: any) => l.id === lineId)?.sku_snapshot.name ?? '';
  const due = new Date(order.delivery_date);
  const today = new Date();
  const lateDays =
    shipDone || order.status === 'CANCELLED'
      ? 0
      : Math.max(
          0,
          Math.round(
            (new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime() -
              new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime()) /
              86400000,
          ),
        );
  const stepDone = [
    ['CONFIRMED', 'CLOSED'].includes(order.status),
    shipDone || delivered,
    delivered,
    settled,
    settled && paid >= Number(order.settlement?.amount ?? 0),
  ];
  const cancelled = order.status === 'CANCELLED';
  // Index of the step in progress; STEPS.length once everything is done.
  const pendingStep = stepDone.findIndex((done) => !done);
  const current = pendingStep === -1 || pendingStep >= steps.length ? steps.length : pendingStep;

  // ---- Actions ---------------------------------------------------------------------------
  const confirmOrder = () =>
    action(
      seller ? '确认接单' : '确认订货',
      [],
      {},
      () => endpoint('confirm', { version: order.version }),
      `请核对 v${order.version} 的商品、数量、价格与交期。确认后将形成你代表本企业的确认记录。`,
    );
  const receive = () => setReceiving(true);
  const requestReturn = () =>
    action(
      '申请退货',
      [
        {
          name: 'receiptId',
          label: '原收货记录',
          options: order.receipts.map((r: any) => ({
            value: r.id,
            label: `${date(r.created_at)} · 实到${amount(r.arrived)} / 合格${amount(r.accepted)}`,
          })),
          wide: true,
        },
        {
          name: 'quality',
          label: '退回库存状态',
          options: [
            { value: 'AVAILABLE', label: '合格库存' },
            { value: 'QUARANTINE', label: '待检库存' },
          ],
        },
        { name: 'quantity', label: '退货数量', decimals: QTY_DECIMALS },
        { name: 'reason', label: '原因', wide: true },
      ],
      { receiptId: order.receipts[0]?.id, quality: 'AVAILABLE', quantity: '', reason: '' },
      (f, key) => endpoint('returns', { ...f, key }),
      '卖方批准后，买方退货出库；卖方收到退货后才增加其库存。双方确认对账后不再受理退货。',
    );
  const reserve = () =>
    action(
      '占用 / 释放订单库存',
      [
        warehouseField,
        {
          name: 'lineId',
          label: '订单商品',
          options: order.lines.map((l: any) => ({
            value: l.id,
            label: `${l.sku_snapshot.name} · ${l.sku_snapshot.spec}`,
          })),
        },
        { name: 'quantity', label: '目标占用数量（0 为释放）', decimals: QTY_DECIMALS },
      ],
      { warehouseId: selectedWarehouse, lineId: order.lines[0].id, quantity: '0' },
      (f) => endpoint('reserve', f),
      '为这张订单预留库存，避免被其他订单先发走。',
    );
  // The printed sheet: the other party, delivery date, goods and the order's mini program code
  // (printed without the code if it cannot be fetched, e.g. offline).
  const printOrder = async () => {
    // A confirmed sale also carries the driver's pickup code.
    const [qr, pickupQr] = await Promise.all([
      imageUrl(`/orders/${id}/mini-code`).catch(() => null),
      seller && order.status === 'CONFIRMED'
        ? api<{ image: string }>(`/orders/${id}/pickup-code`, {})
            .then((r) => r.image)
            .catch(() => null)
        : null,
    ]);
    printer.print({
      title: seller ? '送货单' : '采购单',
      issuer: seller ? order.seller_name : order.buyer_name,
      partyLabel: seller ? '收货单位' : '供货单位',
      party: peer,
      number: order.number,
      orderDate: order.created_at,
      deliveryDate: order.delivery_date,
      note: order.note,
      lines: order.lines.map((l: any) => ({
        name: l.sku_snapshot.name,
        spec: l.sku_snapshot.spec,
        unit: l.sku_snapshot.unit,
        quantity: l.quantity,
        unitPrice: l.unit_price,
        amount: Number(l.quantity) * Number(l.unit_price),
        accessories: accessoriesText(l.accessories, l.quantity),
      })),
      adjust: adjustment
        ? {
            amount: adjustment,
            rate: blendedRate(goods, adjustment),
            reasons: adjustReasons(order.lines.map((l: any) => l.adjust)),
          }
        : null,
      qr,
      qrCaption: seller ? '收货方扫码查看订单' : '微信扫码查看订单',
      pickupQr,
    });
  };
  const cancelOrder = () =>
    action('取消订单', [], {}, () => endpoint('cancel', {}), '取消后订单作废，对方会收到通知。');
  // Proposing (or countering with) an amount counts as our confirmation of it.
  const proposeSettlement = () => {
    const counter = !!order.settlement;
    action(
      counter ? '修改对账金额' : '发起对账',
      [
        { name: 'amount', label: '双方约定结算金额（元）', decimals: 2 },
        { name: 'note', label: '结算依据及调整说明', wide: true },
      ],
      counter
        ? { amount: order.settlement.amount, note: '' }
        : { amount: order.total_amount, note: '按双方确认的交付及差异结果核对' },
      (f) => endpoint('settlement', { ...f, version: order.settlement?.version ?? 0 }),
      counter
        ? `当前 v${order.settlement.version} 为 ${money(order.settlement.amount)}。提交新金额即代表本企业确认 v${order.settlement.version + 1}，需对方重新确认。`
        : '按实收、差异与退货结果提出金额。提交即代表本企业确认，对方确认后金额锁定，不再受理退货。',
    );
  };
  const confirmSettlement = () =>
    action(
      '确认对账金额',
      [],
      {},
      () => endpoint('settlement/confirm', { version: order.settlement.version }),
      `确认 v${order.settlement.version} 对账金额 ${money(order.settlement.amount)}。双方都确认后金额锁定，不再受理退货。`,
    );
  // The supplier records money that arrived without a declaration from the buyer.
  const recordPayment = () =>
    action(
      '登记收款',
      [
        { name: 'amount', label: '金额（元）', decimals: 2 },
        {
          name: 'method',
          label: '收款方式',
          options: Object.entries(PAY_METHODS).map(([value, label]) => ({ value, label })),
        },
        { name: 'reference', label: '凭据号 / 说明', wide: true },
      ],
      { amount: payLeft.toFixed(2), method: 'BANK', reference: '' },
      (f, key) => endpoint('payments', { ...f, key }),
      `尚未结清 ${money(payLeft)}。登记后视为已确认收款，买方账上同步记为已付。`,
    );
  const confirmClaim = (c: any) =>
    action(
      '确认收款',
      [],
      {},
      () => endpoint(`payment-claims/${c.id}`, { action: 'CONFIRM' }),
      `请先核对本企业账户已到账 ${money(c.amount)}（${PAY_METHODS[c.method]}${c.reference ? `，${c.reference}` : ''}）。确认后双方账上同时记为已收 / 已付。`,
    );
  const rejectClaim = (c: any) =>
    action(
      '未收到这笔付款',
      [{ name: 'reason', label: '原因', wide: true }],
      { reason: '' },
      (f) => endpoint(`payment-claims/${c.id}`, { action: 'REJECT', reason: f.reason }),
      `买方声明已付 ${money(c.amount)}（${PAY_METHODS[c.method]}）。请写明未到账或金额不符的情况，买方核对后可重新提交。`,
    );
  const goToIssues = () => {
    issuesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setFlash(true);
    setTimeout(() => setFlash(false), 1600);
  };

  // ---- The one thing to do next ------------------------------------------------------------
  type Next = {
    tone: StatusTone;
    title: string;
    detail: string;
    label?: string;
    run?: () => void;
    disabled?: boolean;
  };
  let next: Next;
  if (order.status === 'CANCELLED')
    next = { tone: 'neutral', title: '订单已取消', detail: '取消前的约定与确认记录保留在下方。' };
  else if (!priced && order.status === 'PENDING')
    next = { tone: 'info', title: '订单待双方确认', detail: '确认后即可安排收发货。' };
  else if (order.status === 'PENDING')
    next = mineConfirmed
      ? {
          tone: 'info',
          title: `等待${peer}确认 v${order.version}`,
          detail: '对方确认后订单生效。需要调整可在“更多操作”中变更约定或取消。',
        }
      : {
          tone: 'warning',
          title: `请核对并确认 v${order.version}`,
          detail: '确认商品、数量、单价和交期无误后确认；有出入可先变更约定。',
          label: seller ? '确认接单' : '确认订货',
          run: confirmOrder,
        };
  else if (myDiscrepancies.length || myReturns.length)
    next = {
      tone: 'warning',
      title: [
        myDiscrepancies.length && `${myDiscrepancies.length} 笔收货差异需要你认可`,
        myReturns.length && `${myReturns.length} 笔退货需要你处理`,
      ]
        .filter(Boolean)
        .join('，'),
      detail: '差异和退货处理完后才能对账。',
      label: '去处理',
      run: goToIssues,
    };
  else if (order.status === 'CONFIRMED')
    next = seller
      ? shipDone
        ? {
            tone: 'info',
            title: `已全部发出，等待${peer}收货验收`,
            detail: '对方登记实收后，差异会出现在“差异与退货”中。',
          }
        : {
            tone: 'warning',
            title: '按实际装车数量登记发货',
            detail: '可分批发货；提交后立即扣减本企业库存。',
            label: '登记发货',
            run: () => setShipping(true),
          }
      : pendingReceipts.length
        ? {
            tone: 'warning',
            title: `${pendingReceipts.length} 批到货待验收`,
            detail: '按实际到货登记实到与合格数量，少货或不合格须填写原因。',
            label: '登记收货',
            run: receive,
          }
        : {
            tone: 'info',
            title: `等待${peer}发货`,
            detail: lateDays ? `已超过约定交期 ${lateDays} 天。` : '对方发货后可在这里登记收货。',
          };
  else if (!priced)
    next = { tone: 'success', title: '交付已完成', detail: '对账与收付款由对账岗位处理。' };
  else if (onStatement && !(settled && paid >= Number(order.settlement.amount)))
    next = {
      tone: 'info',
      title:
        onStatement.status === 'PENDING'
          ? `已纳入对账单 ${onStatement.number}，等待双方确认`
          : `对账单 ${onStatement.number} 待结清`,
      detail: '这张订单随对账单一起确认和付款，付款后按订单先后分摊。',
      label: '查看对账单',
      run: () => navigate(`/statements/${onStatement.id}`),
    };
  else if (!order.settlement)
    next = {
      tone: 'warning',
      title: '交付已完成，可以发起对账',
      detail: '提交金额即代表本企业确认，对方确认后锁定；也可在“对账结算”中与其他订单一起对账。',
      label: '发起对账',
      run: proposeSettlement,
    };
  else if (!settlementMine)
    next = {
      tone: 'warning',
      title: `请核对对账金额 ${money(order.settlement.amount)}`,
      detail: openReturns.length
        ? '还有退货未处理完，完成或驳回后才能确认。'
        : `${peer}已确认这个金额，你确认后锁定；有异议可在“更多操作”中修改金额。`,
      label: '确认对账',
      run: confirmSettlement,
      disabled: openReturns.length > 0,
    };
  else if (!settled)
    next = {
      tone: 'info',
      title: `等待${peer}确认对账`,
      detail: `对账金额 ${money(order.settlement.amount)}（v${order.settlement.version}），对方确认后锁定。`,
    };
  else if (paid < Number(order.settlement.amount))
    next = seller
      ? pendingClaims.length
        ? {
            tone: 'warning',
            title: `${peer}已付款 ${money(pendingClaims[0].amount)}，请核对到账`,
            detail: `${PAY_METHODS[pendingClaims[0].method]}${pendingClaims[0].reference ? ` · ${pendingClaims[0].reference}` : ''}。确认后双方同时记为已结清。`,
            label: '确认收款',
            run: () => confirmClaim(pendingClaims[0]),
          }
        : {
            tone: 'info',
            title: `等待${peer}付款 ${money(payLeft)}`,
            detail: '对方付款后会提交给你确认；线下已收到也可在“更多操作”中直接登记收款。',
          }
      : payLeft > 0
        ? {
            tone: 'warning',
            title: `向${peer}付款 ${money(payLeft)}`,
            detail: pendingTotal
              ? `另有 ${money(pendingTotal)} 已提交，等待对方确认收款。`
              : '查看对方收款账户或收款码，付款后提交由对方确认。',
            label: '去付款',
            run: () => setPaying(true),
          }
        : {
            tone: 'info',
            title: `已提交付款 ${money(pendingTotal)}，等待${peer}确认收款`,
            detail: '对方核对到账后，订单即结清。',
          };
  else
    next = {
      tone: 'success',
      title: '交易已完成，本企业已结清',
      detail: `对账 ${money(order.settlement.amount)}，已全部登记。`,
    };

  const more = [
    priced &&
      ['PENDING', 'CONFIRMED'].includes(order.status) &&
      order.shipments.length === 0 && {
        label: '变更约定',
        hint: '调整数量、单价、交期，可增减商品',
        run: () => setRevising(true),
      },
    seller &&
      order.status === 'CONFIRMED' && {
        label: '库存占用',
        hint: '为本单预留或释放库存',
        run: reserve,
      },
    seller &&
      order.status === 'CONFIRMED' &&
      !shipDone &&
      next.label !== '登记发货' && {
        label: '登记发货',
        hint: '按实际装车数量分批发货',
        run: () => setShipping(true),
      },
    !seller &&
      pendingReceipts.length > 0 &&
      next.label !== '登记收货' && {
        label: '登记收货',
        hint: '登记实到与合格数量',
        run: receive,
      },
    !seller &&
      order.receipts.length > 0 &&
      !settled && { label: '申请退货', hint: '退回已收货物，需卖方批准', run: requestReturn },
    order.settlement &&
      !settled &&
      !onStatement && {
        label: '修改对账金额',
        hint: '提出新金额，需对方重新确认',
        run: proposeSettlement,
      },
    seller &&
      settled &&
      !onStatement &&
      payLeft > 0 && {
        label: '登记收款',
        hint: '线下已收到但买方未提交时使用',
        run: recordPayment,
      },
    priced && {
      label: '分享给合作方',
      hint: '生成小程序码，微信扫码后转发',
      run: () => setSharing(true),
    },
    priced &&
      order.status === 'PENDING' && {
        label: '取消订单',
        hint: '对方确认前可取消',
        run: cancelOrder,
        danger: true,
      },
  ].filter(Boolean) as { label: string; hint: string; run: () => void; danger?: boolean }[];

  // Waiting on the partner reads as plain text on grey: only our turn and completion get colour.
  const toneColor: Record<StatusTone, string> = {
    success: '#1d7348',
    warning: '#9a5b06',
    info: '#24343a',
    neutral: '#5c6c72',
  };
  const toneBg: Record<StatusTone, string> = {
    success: '#eef8f2',
    warning: '#fff7ea',
    info: '#f4f6f7',
    neutral: '#f4f6f7',
  };
  const NextIcon =
    next.tone === 'success'
      ? CheckCircleOutlineOutlined
      : next.tone === 'warning'
        ? ErrorOutlineOutlined
        : null;

  // Shipment rows come one per line; group them back into the batches that left together.
  const batches: { id: string; number: string; created_at: string; pickup: any; lines: any[] }[] =
    [];
  for (const s of order.shipments) {
    const batch = batches.find((b) => b.id === s.shipment_id);
    if (batch) batch.lines.push(s);
    else
      batches.push({
        id: s.shipment_id,
        number: s.number,
        created_at: s.created_at,
        pickup: s.pickup,
        lines: [s],
      });
  }
  const history = [
    ...order.versions.map((v: any) => ({
      at: v.created_at,
      text: v.version === 1 ? '创建订单 v1' : `变更约定为 v${v.version}`,
    })),
    ...order.confirmations.map((c: any) => ({
      at: c.created_at,
      text: `${c.workspace_id === order.seller_id ? '卖方' : '买方'} ${c.display_name} 确认 v${c.version}`,
    })),
    ...order.shipments.map((s: any) => ({
      at: s.created_at,
      text: `发货 ${s.number} · ${amount(s.quantity)}`,
    })),
    ...batches
      .filter((b) => b.pickup)
      .map((b) => ({ at: b.pickup.at, text: `${b.pickup.name || '司机'} 提货 ${b.number}` })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <>
      {/* Back on the left, 打印 on the right, as on the self-recorded order page. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }} className="no-print">
        <Button
          component={Link}
          to={backTo.path}
          size="small"
          startIcon={<ArrowBack />}
          sx={{ mb: 2 }}
        >
          返回{backTo.label}
        </Button>
        {/* The printed sheet carries prices. */}
        {priced && (
          <Button
            size="small"
            startIcon={<PrintOutlined />}
            onClick={() => void printOrder()}
            sx={{ ml: 'auto' }}
          >
            打印
          </Button>
        )}
      </Box>

      {/* Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Tag tone="info">{seller ? '销售订单' : '采购订单'}</Tag>
              <Status value={order.status} />
            </Stack>
            <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>{peer}</Typography>
            {/* The shared number is the one both parties quote; the own number is this
                enterprise's sales / purchase record of the same order, used in its own books. */}
            <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
              协同单号 {order.number}
              {order.ownOrder?.number &&
                ` · 本企业${seller ? '销售' : '采购'}单 ${order.ownOrder.number}`}
              {' · '}约定 v{order.version}
              {order.note && ` · ${order.note}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-end' }}>
            {priced && (
              <Box>
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  订单金额
                </Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
                  {money(total)}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                交期
                {lateDays > 0 && (
                  <Box component="span" sx={{ color: 'error.main', fontWeight: 650, ml: 1 }}>
                    逾期 {lateDays} 天
                  </Box>
                )}
              </Typography>
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: lateDays > 0 ? 'error.main' : 'text.primary',
                }}
              >
                {date(order.delivery_date)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Lifecycle: steps and connectors are siblings in one row, so they never overlap. */}
        {!cancelled && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 3,
              pt: 2.5,
              borderTop: '1px solid #edf1f2',
            }}
          >
            {steps.map((label, i) => {
              const done = stepDone[i];
              const now = i === current;
              return (
                <Box
                  key={label}
                  sx={{ display: 'flex', alignItems: 'center', flex: i ? 1 : 'none', minWidth: 0 }}
                >
                  {i > 0 && (
                    <Box
                      sx={{
                        flex: 1,
                        height: 2,
                        mx: 1.5,
                        minWidth: 12,
                        borderRadius: 1,
                        bgcolor: done || now ? 'primary.main' : '#e3e9eb',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      flex: 'none',
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      bgcolor: done ? 'primary.main' : now ? '#e8f3f0' : '#f1f4f5',
                      color: done ? '#fff' : now ? 'primary.main' : '#9aa8ad',
                      boxShadow: now ? '0 0 0 2px #17665f' : 'none',
                    }}
                  >
                    {done ? '✓' : i + 1}
                  </Box>
                  <Typography
                    noWrap
                    sx={{
                      flex: 'none',
                      ml: 1,
                      fontSize: 13,
                      fontWeight: now ? 700 : 500,
                      color: done ? 'primary.main' : now ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Next step: tinted when it is our turn or finished, plain grey while waiting. */}
        <Box
          sx={{
            mt: 2.5,
            px: 2,
            py: next.label ? 1.5 : 1.25,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexWrap: 'wrap',
            bgcolor: toneBg[next.tone],
          }}
        >
          {NextIcon && <NextIcon sx={{ color: toneColor[next.tone], fontSize: 18 }} />}
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography
              component="span"
              sx={{ fontSize: 14, fontWeight: 650, color: toneColor[next.tone] }}
            >
              {next.title}
            </Typography>
            <Typography component="span" color="text.secondary" sx={{ fontSize: 12.5, ml: 1.5 }}>
              {next.detail}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} className="no-print" sx={{ alignItems: 'center' }}>
            <Button
              size="small"
              color="inherit"
              endIcon={<MoreHorizOutlined />}
              onClick={(e) => setMenu(e.currentTarget)}
              sx={{ color: 'text.secondary' }}
            >
              更多操作
            </Button>
            {next.label && (
              <Button
                variant="contained"
                disableElevation
                disabled={next.disabled}
                startIcon={next.label === '登记发货' ? <LocalShippingOutlined /> : undefined}
                onClick={next.run}
              >
                {next.label}
              </Button>
            )}
          </Stack>
        </Box>
        <Menu
          anchorEl={menu}
          open={!!menu}
          onClose={() => setMenu(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {more.map((m) => (
            <MenuItem
              key={m.label}
              onClick={() => {
                setMenu(null);
                m.run();
              }}
              sx={{ minWidth: 240, color: m.danger ? 'error.main' : undefined }}
            >
              <ListItemText
                primary={m.label}
                secondary={m.hint}
                slotProps={{ secondary: { sx: { fontSize: 12 } } }}
              />
            </MenuItem>
          ))}
        </Menu>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {/* Main column: the goods and their delivery */}
        <Box sx={{ minWidth: 0 }}>
          <Panel title={`商品明细 · ${order.lines.length} 种`}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ minWidth: 200 }}>商品</TableCell>
                    <TableCell align="right">约定数量</TableCell>
                    {priced && <TableCell align="right">单价</TableCell>}
                    {priced && <TableCell align="right">小计</TableCell>}
                    <TableCell sx={{ width: 280, minWidth: 260 }}>交付进度</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.lines.map((l: any) => {
                    const q = Number(l.quantity);
                    const shippedPct = q ? (Number(l.shipped) / q) * 100 : 0;
                    const acceptedPct = q ? (Number(l.accepted) / q) * 100 : 0;
                    const accessories = l.accessories ?? [];
                    return [
                      <TableRow
                        key={l.id}
                        // A product and its accessories read as one group: no rule between them.
                        sx={accessories.length ? { '& td': { borderBottom: 0 } } : undefined}
                      >
                        <TableCell>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {l.sku_snapshot.name}
                          </Typography>
                          <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                            {l.sku_snapshot.spec}
                          </Typography>
                          {l.buyer_sku && (
                            <Typography color="primary" sx={{ fontSize: 12, mt: 0.25 }}>
                              入库为我方：{l.buyer_sku.name}
                              {Number(l.buyer_sku.factor) !== 1 &&
                                `（1 ${l.sku_snapshot.unit} = ${amount(l.buyer_sku.factor)} ${l.buyer_sku.unit}）`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {amount(l.quantity)} {l.sku_snapshot.unit}
                        </TableCell>
                        {priced && (
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {unitPrice(l.unit_price)}
                            {Number(l.adjust_rate) !== 0 && (
                              <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                                原料 {signedPercent(l.adjust_rate)}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        {priced && (
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {money(q * Number(l.unit_price))}
                          </TableCell>
                        )}
                        <TableCell>
                          <DeliveryBar shipped={shippedPct} accepted={acceptedPct} />
                          <Typography
                            color="text.secondary"
                            sx={{ fontSize: 12, mt: 0.75, whiteSpace: 'nowrap' }}
                          >
                            已发 {amount(l.shipped)} · 合格 {amount(l.accepted)}
                            {Number(l.replacement) > 0 && ` · 补发 ${amount(l.replacement)}`}
                            {Number(l.waived) > 0 && ` · 接受差异 ${amount(l.waived)}`}
                          </Typography>
                        </TableCell>
                      </TableRow>,
                      // Shipped with the product as a whole, so they follow its progress.
                      accessories.length > 0 && (
                        <TableRow key={`${l.id}-accessories`}>
                          <TableCell colSpan={priced ? 5 : 3} sx={{ pt: 0, pb: 1.5 }}>
                            <AccessoryPanel
                              accessories={accessories}
                              productUnit={l.sku_snapshot.unit}
                              pieces={l.quantity}
                              totalLabel="本单共"
                            />
                          </TableCell>
                        </TableRow>
                      ),
                    ];
                  })}
                </TableBody>
                {priced && (
                  <TableFooter>
                    {adjustment !== 0 && (
                      <AdjustRows
                        goods={goods}
                        adjustment={adjustment}
                        reasons={adjustReasons(order.lines.map((l: any) => l.adjust))}
                        span={3}
                      />
                    )}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontSize: 13, color: 'text.secondary' }}>
                        合计
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: 'text.primary',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {money(total)}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}
                      >
                        浅色为已发，深色为合格实收
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </Box>
          </Panel>

          <Panel title="交付记录">
            {batches.length ? (
              batches.map((b) => {
                const received = b.lines.every((s: any) => s.finalized);
                return (
                  <Box key={b.id} sx={{ px: 3, py: 2, borderBottom: '1px solid #edf1f2' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1, minWidth: 0 }}>
                        {b.number} · {date(b.created_at)} 发货
                      </Typography>
                      <Tag tone={received ? 'success' : 'info'}>
                        {received ? '已验收' : '在途 / 待验收'}
                      </Tag>
                    </Box>
                    {b.lines.map((s: any) => (
                      <Typography key={s.id} sx={{ fontSize: 12.5, mt: 0.75 }}>
                        {lineName(s.trade_line_id)}
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {' '}
                          · 发 {amount(s.quantity)} · 实到 {amount(s.arrived)} · 合格{' '}
                          {amount(s.accepted)}
                        </Box>
                      </Typography>
                    ))}
                    {/* Who took it: the seller's own record, not shown to the buyer. */}
                    {/* Older or already received batches without a pickup need no prompt. */}
                    {seller && (b.pickup || !received) && (
                      <Typography
                        sx={{
                          fontSize: 12,
                          mt: 0.75,
                          color: b.pickup ? 'text.secondary' : 'text.disabled',
                        }}
                      >
                        {b.pickup ? `提货：${pickupText(b.pickup)}` : '待司机扫码确认提货'}
                      </Typography>
                    )}
                  </Box>
                );
              })
            ) : (
              <Empty>
                {order.status === 'CONFIRMED'
                  ? seller
                    ? '尚未发货，可在上方登记发货。'
                    : `等待${peer}发货。`
                  : '订单确认后可发货。'}
              </Empty>
            )}
          </Panel>

          {(order.discrepancies.length > 0 || order.returns.length > 0) && (
            <div ref={issuesRef}>
              <Panel
                title="差异与退货"
                highlight={flash}
                extra={
                  myDiscrepancies.length + myReturns.length > 0 ? (
                    <Tag tone="warning">{myDiscrepancies.length + myReturns.length} 项待你处理</Tag>
                  ) : undefined
                }
              >
                {order.discrepancies.map((d: any) => {
                  const mine = !(seller ? d.seller_approved : d.buyer_approved);
                  const both = d.seller_approved && d.buyer_approved;
                  return (
                    <Box
                      key={d.id}
                      sx={{
                        px: 3,
                        py: 2,
                        borderBottom: '1px solid #edf1f2',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                            收货差异 {amount(d.quantity)}
                          </Typography>
                          <Tag tone={both ? 'success' : 'warning'}>
                            {both
                              ? `已${statusLabels[d.resolution] ?? '处理'}`
                              : d.resolution
                                ? `${statusLabels[d.resolution]} · 待${d.seller_approved ? '买方' : '卖方'}认可`
                                : '待协商'}
                          </Tag>
                        </Stack>
                        <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
                          {d.reason}
                        </Typography>
                      </Box>
                      {mine && (
                        <Stack direction="row" spacing={1} className="no-print">
                          {(!d.resolution || d.resolution === 'ACCEPT_LOSS') && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                p.safe(() =>
                                  endpoint('discrepancies', {
                                    id: d.id,
                                    resolution: 'ACCEPT_LOSS',
                                  }),
                                )
                              }
                            >
                              接受差异
                            </Button>
                          )}
                          {(!d.resolution || d.resolution === 'REPLACE') && (
                            <Button
                              size="small"
                              variant="contained"
                              disableElevation
                              onClick={() =>
                                p.safe(() =>
                                  endpoint('discrepancies', { id: d.id, resolution: 'REPLACE' }),
                                )
                              }
                            >
                              同意补发
                            </Button>
                          )}
                        </Stack>
                      )}
                    </Box>
                  );
                })}
                {order.returns.map((r: any) => (
                  <Box
                    key={r.id}
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: '1px solid #edf1f2',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                          退货 {amount(r.quantity)}
                        </Typography>
                        <Status value={r.status} />
                      </Stack>
                      <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
                        {r.reason}
                        {r.status === 'REJECTED' &&
                          r.rejection_reason &&
                          ` · 驳回：${r.rejection_reason}`}
                        {settled &&
                          ['REQUESTED', 'APPROVED'].includes(r.status) &&
                          ` · 已对账，${seller ? '只能驳回' : '等待卖方驳回'}`}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} className="no-print">
                      {seller && ['REQUESTED', 'APPROVED'].includes(r.status) && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            action(
                              '驳回退货申请',
                              [{ name: 'reason', label: '驳回原因', wide: true }],
                              { reason: settled ? '双方已确认对账，不再受理退货' : '' },
                              (f, key) =>
                                endpoint(`returns/${r.id}`, {
                                  action: 'REJECT',
                                  reason: f.reason,
                                  key,
                                }),
                              `退货 ${amount(r.quantity)} · ${r.reason}。驳回后买方不能再对这笔申请退货出库。`,
                            )
                          }
                        >
                          驳回
                        </Button>
                      )}
                      {((seller && r.status === 'REQUESTED' && !settled) ||
                        (!seller && r.status === 'APPROVED' && !settled) ||
                        (seller && r.status === 'SHIPPED')) && (
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          onClick={() => {
                            const actionType =
                              r.status === 'REQUESTED'
                                ? 'APPROVE'
                                : r.status === 'APPROVED'
                                  ? 'SHIP'
                                  : 'RECEIVE';
                            action(
                              actionType === 'APPROVE'
                                ? '批准退货'
                                : actionType === 'SHIP'
                                  ? '退货出库'
                                  : '验收退回货物',
                              actionType === 'APPROVE'
                                ? []
                                : [
                                    warehouseField,
                                    ...(actionType === 'RECEIVE'
                                      ? [
                                          {
                                            name: 'quality',
                                            label: '收回状态',
                                            options: [
                                              { value: 'QUARANTINE', label: '待检隔离' },
                                              { value: 'AVAILABLE', label: '已检合格' },
                                            ],
                                          },
                                        ]
                                      : []),
                                  ],
                              { warehouseId: selectedWarehouse, quality: 'QUARANTINE' },
                              (f, key) =>
                                endpoint(`returns/${r.id}`, { ...f, action: actionType, key }),
                            );
                          }}
                        >
                          {r.status === 'REQUESTED'
                            ? '批准退货'
                            : r.status === 'APPROVED'
                              ? '退货出库'
                              : '验收入库'}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Panel>
            </div>
          )}
        </Box>

        {/* Side column: money, parties, files, history */}
        <Box sx={{ minWidth: 0 }}>
          {priced && (
            <Panel
              title="对账与收付款"
              extra={
                !order.settlement ? (
                  <Tag>尚未对账</Tag>
                ) : !settled ? (
                  <Tag tone="warning">对账待确认</Tag>
                ) : paid >= Number(order.settlement.amount) ? (
                  <Tag tone="success">本企业已结清</Tag>
                ) : (
                  <Tag tone="info">已对账 · 未结清</Tag>
                )
              }
            >
              <Box sx={{ p: 3 }}>
                {order.settlement ? (
                  <>
                    <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                      对账金额 · v{order.settlement.version}
                    </Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
                      {money(order.settlement.amount)}
                    </Typography>
                    {order.settlement.note && (
                      <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
                        {order.settlement.note}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Tag tone={order.settlement.seller_confirmed ? 'success' : 'warning'}>
                        卖方{order.settlement.seller_confirmed ? '已确认' : '待确认'}
                      </Tag>
                      <Tag tone={order.settlement.buyer_confirmed ? 'success' : 'warning'}>
                        买方{order.settlement.buyer_confirmed ? '已确认' : '待确认'}
                      </Tag>
                    </Stack>
                    {settled && (
                      <Box sx={{ mt: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                          <Typography sx={{ fontSize: 12.5 }}>
                            已{seller ? '收' : '付'} {money(paid)}
                          </Typography>
                          <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                            余 {money(Math.max(0, Number(order.settlement.amount) - paid))}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(
                            100,
                            (paid / Math.max(Number(order.settlement.amount), 0.01)) * 100,
                          )}
                          color="success"
                          sx={{ height: 6, borderRadius: 3, bgcolor: '#edf1f2' }}
                        />
                      </Box>
                    )}
                    {claims.length > 0 && (
                      <Stack spacing={1.25} sx={{ mt: 2 }}>
                        {claims.map((c) => (
                          <Box key={c.id} sx={{ p: 1.5, borderRadius: 1, bgcolor: '#f6f8f8' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 650, flex: 1 }}>
                                {money(c.amount)} · {PAY_METHODS[c.method]}
                              </Typography>
                              <Tag
                                tone={
                                  c.status === 'CONFIRMED'
                                    ? 'success'
                                    : c.status === 'REJECTED'
                                      ? 'neutral'
                                      : 'warning'
                                }
                              >
                                {c.status === 'CONFIRMED'
                                  ? '已确认收款'
                                  : c.status === 'REJECTED'
                                    ? '未收到'
                                    : '待确认收款'}
                              </Tag>
                            </Box>
                            <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
                              {date(c.created_at)}
                              {c.reference && ` · ${c.reference}`}
                              {c.status === 'REJECTED' &&
                                c.reject_reason &&
                                ` · ${c.reject_reason}`}
                            </Typography>
                            {seller && c.status === 'CLAIMED' && (
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 1fr',
                                  gap: 1,
                                  mt: 1.5,
                                }}
                                className="no-print"
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  onClick={() => rejectClaim(c)}
                                  sx={{
                                    borderColor: '#cfd9dc',
                                    color: 'text.primary',
                                    bgcolor: '#fff',
                                  }}
                                >
                                  未收到
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disableElevation
                                  onClick={() => confirmClaim(c)}
                                >
                                  确认收款
                                </Button>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    )}
                    {onStatement && (
                      <Button
                        component={Link}
                        to={`/statements/${onStatement.id}`}
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 2 }}
                        className="no-print"
                      >
                        对账单 {onStatement.number}
                      </Button>
                    )}
                    {settled && !seller && !onStatement && payLeft > 0 && (
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 2 }}
                        className="no-print"
                        onClick={() => setPaying(true)}
                      >
                        查看收款账户并付款
                      </Button>
                    )}
                    <Typography color="text.secondary" sx={{ fontSize: 11.5, mt: 2 }}>
                      款项在双方之间线下支付，平台只记录，不经手资金。
                    </Typography>
                  </>
                ) : (
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    {delivered
                      ? '交付已完成，可在上方发起对账。'
                      : '交付完成后发起对账；对账确认后金额锁定，不再受理退货。'}
                  </Typography>
                )}
              </Box>
            </Panel>
          )}

          <Panel title={`双方确认 · 当前 v${order.version}`}>
            <Box sx={{ px: 3, py: 1 }}>
              {[
                ['卖方', order.seller_name, order.seller_confirmed, order.seller_id],
                ['买方', order.buyer_name, order.buyer_confirmed, order.buyer_id],
              ].map(([role, name, ok, wid]) => {
                const who = order.confirmations.find(
                  (c: any) => c.workspace_id === wid && c.version === order.version,
                );
                return (
                  <Box
                    key={String(role)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>
                        {name}
                        {wid === p.workspace && (
                          <Typography component="span" color="text.secondary" sx={{ fontSize: 12 }}>
                            {' '}
                            （本企业）
                          </Typography>
                        )}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                        {role}
                        {who && ` · ${who.display_name} ${date(who.created_at)}`}
                      </Typography>
                    </Box>
                    <Tag tone={ok ? 'success' : 'warning'}>{ok ? '已确认' : '待确认'}</Tag>
                  </Box>
                );
              })}
            </Box>
          </Panel>

          <Panel
            title="附件"
            extra={
              <Button component="label" size="small" className="no-print">
                上传
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file)
                      p.safe(async () => {
                        const form = new FormData();
                        form.append('file', file);
                        await endpoint('attachments', form);
                      });
                    e.target.value = '';
                  }}
                />
              </Button>
            }
          >
            {files.length ? (
              files.map((f) => (
                <Box
                  key={f.id}
                  sx={{
                    px: 3,
                    py: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: '1px solid #edf1f2',
                  }}
                >
                  <Button
                    size="small"
                    sx={{ minWidth: 0, flex: 1, justifyContent: 'flex-start' }}
                    onClick={() => p.safe(() => download(`/attachments/${f.id}`, f.name))}
                  >
                    <Typography noWrap sx={{ fontSize: 13 }}>
                      {f.name}
                    </Typography>
                  </Button>
                  <Typography color="text.secondary" sx={{ fontSize: 11 }}>
                    {Math.ceil(f.size / 1024)} KB
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography color="text.secondary" sx={{ fontSize: 12.5, px: 3, py: 2.5 }}>
                可上传收货差异照片或交易凭据（图片 / PDF，最多 5MB）。
              </Typography>
            )}
          </Panel>

          <Panel title="操作记录">
            <Box sx={{ px: 3, py: 2 }}>
              {history.map((h, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, pb: 1.5 }}>
                  <Box
                    sx={{
                      mt: 0.75,
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: i === 0 ? 'primary.main' : '#c7d2d5',
                      flex: 'none',
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: 12.5 }}>{h.text}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 11.5 }}>
                      {new Date(h.at).toLocaleString('zh-CN')}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Panel>
        </Box>
      </Box>

      {shipping && (
        <LineQuantityDialog
          title="确认实际出库"
          subtitle={`${order.number} · ${order.buyer_name} · 按实际装车数量填写，可分批发货`}
          verb="发货"
          lines={order.lines.map((l: any) => ({
            id: l.id,
            name: l.sku_snapshot.name,
            spec: l.sku_snapshot.spec,
            accessories: l.accessories,
            unit: l.sku_snapshot.unit,
            ordered: l.quantity,
            done: l.shipped,
            // Agreed replacements may be shipped on top of the original quantity.
            remaining: shipLeft(l),
            skuId: l.seller_sku_id,
          }))}
          warehouses={p.warehouses}
          footnote="提交后立即扣减本企业库存，买方仍需实际验收。"
          submit={(warehouseId, lines, key) => endpoint('ship', { warehouseId, lines, key })}
          onClose={() => setShipping(false)}
          onDone={() => {
            setShipping(false);
            p.reload();
            p.notify('已登记发货');
          }}
        />
      )}
      {printer.sheet}
      {sharing && (
        <ShareCodeDialog
          orderId={order.id}
          number={order.number}
          onClose={() => setSharing(false)}
          notify={p.notify}
        />
      )}
      {receiving && (
        <ReceiveDialog
          order={order}
          warehouses={p.warehouses}
          onClose={() => setReceiving(false)}
          onDone={() => {
            setReceiving(false);
            p.reload();
            p.notify('已登记收货');
          }}
        />
      )}
      {paying && (
        <PayDialog
          path={`/orders/${order.id}/payment-claims`}
          peer={peer}
          left={payLeft}
          payee={order.payee}
          onClose={() => setPaying(false)}
          onDone={() => {
            setPaying(false);
            p.reload();
            p.notify('已提交付款，等待对方确认收款');
          }}
        />
      )}
      {revising && (
        <ReviseDialog
          order={order}
          seller={seller}
          onClose={() => setRevising(false)}
          onDone={() => {
            setRevising(false);
            p.reload();
            p.notify('已提交变更，等待对方确认');
          }}
        />
      )}
    </>
  );
}
